import type { AnimationPreset } from "../../scene/schema";
import type { BlockNode, LibraryBlock } from "../build";
import {
  BLACK_GLASS,
  ellipse,
  icon,
  motion,
  photo,
  pill,
  rect,
  stack,
  type TemplateNode,
  text,
  WHITE,
} from "./helpers";

const GAP = 24;

function grid(
  area: { width: number; height: number },
  cells: { x: number; y: number; w: number; h: number }[],
  options: {
    radius?: "sm" | "md" | "lg" | "none";
    stagger?: number;
    preset?: AnimationPreset;
  } = {},
): TemplateNode[] {
  // Every cell gives up one gap on its right and bottom; with the outer margin all gaps are equal.
  const width = area.width - GAP;
  const height = area.height - GAP;
  return cells.map((cell, index) =>
    photo({
      x: GAP + cell.x * width,
      y: GAP + cell.y * height,
      w: cell.w * width - GAP,
      h: cell.h * height - GAP,
      radius: options.radius ?? "sm",
      name: `Bild ${index + 1}`,
      ...motion(options.preset ?? "zoom", { delay: index * (options.stagger ?? 150) }),
    }),
  );
}

/** A cell of a collage in fractions of the page. */
const cell = (x: number, y: number, w: number, h: number) => ({ x, y, w, h });

export const COLLAGES: LibraryBlock[] = [
  {
    id: "collage-2-nebeneinander",
    title: "2 nebeneinander",
    category: "collage",
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(area, [cell(0, 0, 0.5, 1), cell(0.5, 0, 0.5, 1)], { preset: "slide-down" }),
  },
  {
    id: "collage-2-uebereinander",
    title: "2 übereinander",
    category: "collage",
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(area, [cell(0, 0, 1, 0.5), cell(0, 0.5, 1, 0.5)], {
        preset: "slide-right",
      }),
  },
  {
    id: "collage-1-2",
    title: "1 groß + 2 klein",
    category: "collage",
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(
        area,
        area.height >= area.width
          ? [cell(0, 0, 1, 0.6), cell(0, 0.6, 0.5, 0.4), cell(0.5, 0.6, 0.5, 0.4)]
          : [cell(0, 0, 0.6, 1), cell(0.6, 0, 0.4, 0.5), cell(0.6, 0.5, 0.4, 0.5)],
      ),
  },
  {
    id: "collage-raster-4",
    title: "Raster 2 × 2",
    category: "collage",
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(
        area,
        [
          cell(0, 0, 0.5, 0.5),
          cell(0.5, 0, 0.5, 0.5),
          cell(0, 0.5, 0.5, 0.5),
          cell(0.5, 0.5, 0.5, 0.5),
        ],
        { preset: "pop" },
      ),
  },
  {
    id: "collage-mosaik",
    title: "Mosaik",
    category: "collage",
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(
        area,
        [
          cell(0, 0, 0.6, 0.5),
          cell(0.6, 0, 0.4, 0.25),
          cell(0.6, 0.25, 0.4, 0.25),
          cell(0, 0.5, 0.4, 0.5),
          cell(0.4, 0.5, 0.6, 0.5),
        ],
        { stagger: 120 },
      ),
  },
  {
    id: "collage-polaroids",
    title: "Polaroids",
    category: "collage",
    width: 1000,
    height: 900,
    anchor: "center",
    duration: 5000,
    nodes: [
      ...polaroid(40, 140, -7, 0),
      ...polaroid(520, 90, 5, 250),
      ...polaroid(280, 420, -2, 500),
    ],
  },
  {
    id: "collage-filmstreifen",
    title: "Filmstreifen",
    category: "collage",
    width: 520,
    height: 1260,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 520,
        h: 1260,
        fill: "#111111",
        name: "Streifen",
        ...motion("slide-down"),
      }),
      ...Array.from({ length: 14 }, (_, index): TemplateNode[] => [
        rect({
          x: 16,
          y: 20 + index * 88,
          w: 30,
          h: 44,
          fill: "#f5f5f4",
          radius: 6,
          name: "Loch",
          ...motion("slide-down"),
        }),
        rect({
          x: 474,
          y: 20 + index * 88,
          w: 30,
          h: 44,
          fill: "#f5f5f4",
          radius: 6,
          name: "Loch",
          ...motion("slide-down"),
        }),
      ]).flat(),
      ...[0, 1, 2].map((index) =>
        photo({
          x: 70,
          y: 40 + index * 400,
          w: 380,
          h: 380,
          name: `Bild ${index + 1}`,
          ...motion("fade", { delay: 300 + index * 250 }),
        }),
      ),
    ],
  },
  {
    id: "collage-kreis",
    title: "Rundes Foto",
    category: "collage",
    width: 700,
    height: 860,
    anchor: "center",
    duration: 5000,
    nodes: [
      ellipse({
        x: 20,
        y: 0,
        w: 660,
        h: 660,
        fill: "#00000000",
        stroke: { color: "accent", width: 14 },
        name: "Ring",
        ...motion("spin", { duration: 800 }),
      }),
      photo({
        x: 50,
        y: 30,
        w: 600,
        h: 600,
        ellipse: true,
        name: "Bild",
        ...motion("zoom", { delay: 200 }),
      }),
      text({
        text: "Das sind wir",
        style: "heading-2",
        size: 64,
        color: "text",
        x: 0,
        y: 700,
        w: 700,
        align: "center",
        ...motion("slide-up", { delay: 450 }),
      }),
    ],
  },
  {
    id: "collage-raster-9",
    title: "Raster 3 × 3",
    category: "collage",
    tags: ["Raster", "Grid", "neun Bilder", "Galerie"],
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(
        area,
        [0, 1, 2].flatMap((row) => [0, 1, 2].map((col) => cell(col / 3, row / 3, 1 / 3, 1 / 3))),
        { preset: "pop", stagger: 80 },
      ),
  },
  {
    id: "collage-3-streifen",
    title: "3 Streifen",
    category: "collage",
    tags: ["drei Bilder", "Spalten", "Reihen", "Triptychon"],
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(
        area,
        [0, 1, 2].map((index) =>
          area.height >= area.width * 1.2
            ? cell(0, index / 3, 1, 1 / 3)
            : cell(index / 3, 0, 1 / 3, 1),
        ),
        { preset: area.height >= area.width * 1.2 ? "slide-right" : "slide-down" },
      ),
  },
  {
    id: "collage-1-3",
    title: "1 groß + 3 klein",
    category: "collage",
    tags: ["vier Bilder", "Hauptbild", "Galerie"],
    width: 1080,
    height: 1080,
    anchor: "fill",
    duration: 5000,
    nodes: (area) =>
      grid(
        area,
        area.height >= area.width
          ? [cell(0, 0, 1, 0.62), ...[0, 1, 2].map((index) => cell(index / 3, 0.62, 1 / 3, 0.38))]
          : [cell(0, 0, 0.62, 1), ...[0, 1, 2].map((index) => cell(0.62, index / 3, 0.38, 1 / 3))],
        { stagger: 140 },
      ),
  },
  {
    id: "collage-magazin",
    title: "Magazin mit Titel",
    category: "collage",
    tags: ["Magazin", "Cover", "Titelseite", "Editorial", "Zeitschrift"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 5500,
    nodes: ({ width, height }) => {
      const portrait = height >= width;
      const big = portrait
        ? { x: GAP, y: GAP, w: width - GAP * 2, h: height * 0.6 }
        : { x: GAP, y: GAP, w: width * 0.6, h: height - GAP * 2 };
      const copy = portrait
        ? { x: 60, y: big.y + big.h + 50, w: width * 0.56 - 60 }
        : { x: big.x + big.w + 60, y: 70, w: width - big.w - GAP - 120 };
      const small = portrait
        ? {
            x: width * 0.6,
            y: big.y + big.h + GAP,
            w: width * 0.4 - GAP,
            h: height - big.h - GAP * 3,
          }
        : {
            x: big.x + big.w + GAP,
            y: height * 0.52,
            w: width - big.w - GAP * 3,
            h: height * 0.48 - GAP,
          };
      return [
        rect({
          x: 0,
          y: 0,
          w: width,
          h: height,
          fill: "background",
          name: "Hintergrund",
          ...motion("fade", { duration: 300 }),
        }),
        photo({ ...big, radius: "sm", name: "Titelbild", ...motion("zoom", { duration: 700 }) }),
        stack({
          x: copy.x,
          y: copy.y,
          w: copy.w,
          gap: 16,
          items: [
            {
              kind: "text",
              text: "AUSGABE 07 · JULI",
              style: "subline",
              size: 28,
              color: "primary",
              ...motion("fade", { delay: 300 }),
            },
            {
              kind: "text",
              text: "Sommer in der Stadt",
              style: "heading-1",
              size: portrait ? Math.min(96, (height - big.h) * 0.16) : 96,
              color: "text",
              ...motion("slide-up", { delay: 400, duration: 600 }),
            },
          ],
        }),
        photo({ ...small, radius: "sm", name: "Bild klein", ...motion("zoom", { delay: 550 }) }),
      ];
    },
  },
  {
    id: "collage-versetzt",
    title: "Versetzt",
    category: "collage",
    tags: ["überlappend", "diagonal", "Fotos", "locker", "Stapel"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 5000,
    nodes: ({ width, height }) => {
      const portrait = height >= width * 1.2;
      const w = portrait ? Math.min(width, height) * 0.5 : height * 0.42;
      const h = portrait ? w * 1.2 : height * 0.5;
      const spots = portrait
        ? [
            { cx: 0.3, cy: 0.28, rotation: -4 },
            { cx: 0.62, cy: 0.5, rotation: 3 },
            { cx: 0.36, cy: 0.74, rotation: -2 },
          ]
        : [
            { cx: 0.25, cy: 0.35, rotation: -4 },
            { cx: 0.5, cy: 0.62, rotation: 3 },
            { cx: 0.75, cy: 0.38, rotation: -2 },
          ];
      return spots.flatMap((spot, index): BlockNode[] => {
        const x = spot.cx * width - w / 2;
        const y = spot.cy * height - h / 2;
        const move = motion("drop", { delay: index * 250, duration: 800 });
        return [
          rect({
            x: x - 16,
            y: y - 16,
            w: w + 32,
            h: h + 32,
            fill: "#ffffff",
            rotation: spot.rotation,
            shadow: { color: "#00000040", y: 14, blur: 36 },
            name: "Rahmen",
            ...move,
          }),
          photo({
            x,
            y,
            w,
            h,
            rotation: spot.rotation,
            radius: "none",
            name: `Bild ${index + 1}`,
            ...move,
          }),
        ];
      });
    },
  },
  {
    id: "collage-streifen-text",
    title: "Bildstreifen mit Text",
    category: "collage",
    tags: ["drei Bilder", "Überschrift", "Highlights", "Reihe"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 5500,
    nodes: ({ width, height }) => {
      const portrait = height >= width * 1.2;
      const w = (width - GAP * 4) / 3;
      const h = portrait ? w * 1.5 : Math.min(w * 1.1, height * 0.5);
      const y = portrait ? height / 2 - h / 2 : height * 0.3;
      return [
        rect({
          x: 0,
          y: 0,
          w: width,
          h: height,
          fill: "background",
          name: "Hintergrund",
          ...motion("fade", { duration: 300 }),
        }),
        stack({
          x: 60,
          y: y - 50,
          w: width - 120,
          anchor: "bottom",
          align: "center",
          items: [
            {
              kind: "text",
              text: "Unsere Highlights",
              style: "heading-1",
              size: 96,
              color: "text",
              ...motion("slide-down"),
            },
          ],
        }),
        ...[0, 1, 2].map((index) =>
          photo({
            x: GAP + index * (w + GAP),
            y,
            w,
            h,
            radius: "sm",
            name: `Bild ${index + 1}`,
            ...motion("slide-up", { delay: 200 + index * 150 }),
          }),
        ),
        text({
          text: "Die schönsten Momente der Woche",
          style: "subline",
          size: 40,
          color: "text-muted",
          x: 60,
          y: y + h + 50,
          w: width - 120,
          align: "center",
          ...motion("fade", { delay: 700 }),
        }),
      ];
    },
  },
  {
    id: "collage-kreise",
    title: "Kreise",
    category: "collage",
    tags: ["rund", "Kreis", "drei Bilder", "verspielt"],
    width: 1000,
    height: 1000,
    anchor: "center",
    duration: 5000,
    nodes: [
      ellipse({
        x: 760,
        y: 150,
        w: 110,
        h: 110,
        fill: "accent",
        name: "Punkt",
        ...motion("pop", { delay: 700 }),
      }),
      ...[
        { x: 40, y: 40, size: 600 },
        { x: 600, y: 420, size: 360 },
        { x: 170, y: 680, size: 280 },
      ].flatMap((circle, index): BlockNode[] => {
        const move = motion("zoom", { delay: index * 220 });
        return [
          ellipse({
            x: circle.x - 14,
            y: circle.y - 14,
            w: circle.size + 28,
            h: circle.size + 28,
            fill: "background",
            shadow: { color: "#00000033", y: 10, blur: 30 },
            name: "Ring",
            ...move,
          }),
          photo({
            x: circle.x,
            y: circle.y,
            w: circle.size,
            h: circle.size,
            ellipse: true,
            name: `Bild ${index + 1}`,
            ...move,
          }),
        ];
      }),
    ],
  },
  {
    id: "collage-scrapbook",
    title: "Scrapbook",
    category: "collage",
    tags: ["Klebeband", "Erinnerungen", "Fotoalbum", "Urlaub", "Polaroid"],
    width: 1000,
    height: 1120,
    anchor: "center",
    duration: 5500,
    nodes: [
      ...[
        { x: 20, y: 40, rotation: -6 },
        { x: 530, y: 20, rotation: 5 },
        { x: 40, y: 560, rotation: 4 },
        { x: 520, y: 540, rotation: -5 },
      ].flatMap((spot, index) => [
        ...polaroid(spot.x, spot.y, spot.rotation, index * 250),
        tape(spot.x, spot.y, spot.rotation, index * 250 + 350),
      ]),
      rect({
        x: 330,
        y: 1010,
        w: 340,
        h: 100,
        fill: "#ffffff",
        rotation: -2,
        shadow: { color: "#00000033", y: 6, blur: 18 },
        name: "Etikett",
        ...motion("pop", { delay: 1300 }),
      }),
      text({
        text: "Unser Sommer",
        style: "quote",
        size: 54,
        color: "#27272a",
        x: 340,
        y: 1010,
        w: 320,
        h: 100,
        align: "center",
        rotation: -2,
        ...motion("pop", { delay: 1300 }),
      }),
    ],
  },
  {
    id: "collage-vorher-nachher-trenner",
    title: "Vorher / Nachher mit Trenner",
    category: "collage",
    tags: ["Vergleich", "Vorher Nachher", "Transformation", "Split", "Schieberegler"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 6000,
    nodes: ({ width, height }) => {
      const portrait = height >= width * 1.2;
      const half = portrait ? height / 2 : width / 2;
      const line = 8;
      const knob = 110;
      const m = 50;
      return [
        photo({
          x: 0,
          y: 0,
          w: portrait ? width : half,
          h: portrait ? half : height,
          radius: "none",
          name: "Vorher",
          ...motion("fade"),
        }),
        photo({
          x: portrait ? 0 : half,
          y: portrait ? half : 0,
          w: portrait ? width : width - half,
          h: portrait ? height - half : height,
          radius: "none",
          name: "Nachher",
          ...motion("fade", { delay: 200 }),
        }),
        rect({
          x: portrait ? 0 : half - line / 2,
          y: portrait ? half - line / 2 : 0,
          w: portrait ? width : line,
          h: portrait ? line : height,
          fill: WHITE,
          name: "Trenner",
          ...motion(portrait ? "slide-right" : "slide-down", { delay: 400 }),
        }),
        ellipse({
          x: (portrait ? width / 2 : half) - knob / 2,
          y: (portrait ? half : height / 2) - knob / 2,
          w: knob,
          h: knob,
          fill: WHITE,
          shadow: { color: "#00000059", y: 6, blur: 20 },
          name: "Regler",
          ...motion("pop", { delay: 600 }),
        }),
        icon({
          icon: "arrows-left-right",
          x: (portrait ? width / 2 : half) - 32,
          y: (portrait ? half : height / 2) - 32,
          w: 64,
          h: 64,
          color: "#18181b",
          rotation: portrait ? 90 : 0,
          name: "Pfeile",
          ...motion("pop", { delay: 600 }),
        }),
        ...pill(
          m,
          portrait ? height * 0.13 : m,
          250,
          72,
          "VORHER",
          BLACK_GLASS,
          28,
          motion("fly-right", { delay: 800 }),
          {
            color: WHITE,
          },
        ),
        ...pill(
          portrait ? m : width - m - 270,
          portrait ? half + m : m,
          270,
          72,
          "NACHHER",
          "accent",
          28,
          motion(portrait ? "fly-right" : "fly-left", { delay: 950 }),
        ),
      ];
    },
  },
  {
    id: "collage-story",
    title: "Story mit Überschrift",
    category: "collage",
    tags: ["Story", "vier Bilder", "Raster", "Tagebuch", "Überschrift"],
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 5500,
    nodes: ({ width, height }) => {
      const header = Math.min(height * 0.2, 260);
      const top = height >= width * 1.2 ? height * 0.1 : 0;
      const gridTop = top + header;
      const cellW = (width - GAP * 3) / 2;
      const cellH = (height - gridTop - GAP * 2) / 2;
      return [
        rect({
          x: 0,
          y: 0,
          w: width,
          h: height,
          fill: "background",
          name: "Hintergrund",
          ...motion("fade", { duration: 300 }),
        }),
        text({
          text: "Mein Tag in Bildern",
          style: "heading-1",
          size: 84,
          color: "text",
          x: 60,
          y: top,
          w: width - 120,
          h: header,
          align: "center",
          ...motion("slide-down"),
        }),
        ...[0, 1, 2, 3].map((index) =>
          photo({
            x: GAP + (index % 2) * (cellW + GAP),
            y: gridTop + Math.floor(index / 2) * (cellH + GAP),
            w: cellW,
            h: cellH,
            radius: "md",
            name: `Bild ${index + 1}`,
            ...motion("pop", { delay: 200 + index * 150 }),
          }),
        ),
      ];
    },
  },
];

/** A photo in a white frame with a wider bottom edge, tilted by `rotation`. */
function polaroid(x: number, y: number, rotation: number, delay: number): TemplateNode[] {
  // The photo sits 40 px above the frame's centre; both turn around their own centre.
  const angle = (rotation * Math.PI) / 180;
  const cx = x + 220 + 40 * Math.sin(angle);
  const cy = y + 250 - 40 * Math.cos(angle);
  return [
    rect({
      x,
      y,
      w: 440,
      h: 500,
      fill: "#ffffff",
      rotation,
      name: "Rahmen",
      shadow: { color: "#00000040", y: 14, blur: 36 },
      ...motion("drop", { delay, duration: 800 }),
    }),
    photo({
      x: cx - 190,
      y: cy - 180,
      w: 380,
      h: 360,
      rotation,
      radius: "none",
      name: "Bild",
      ...motion("drop", { delay, duration: 800 }),
    }),
  ];
}

/** A strip of washi tape across the top edge of a `polaroid` at the same spot. */
function tape(x: number, y: number, rotation: number, delay: number): TemplateNode {
  const angle = (rotation * Math.PI) / 180;
  const cx = x + 220 + 250 * Math.sin(angle);
  const cy = y + 250 - 250 * Math.cos(angle);
  return rect({
    x: cx - 85,
    y: cy - 23,
    w: 170,
    h: 46,
    fill: "#fde68acc",
    rotation: rotation * -1.6,
    name: "Klebeband",
    ...motion("fade", { delay }),
  });
}
